import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Target, Milestone, Calendar, Trash2, Edit2, Sparkles, ChevronRight } from 'lucide-react';

export default function LifeMap() {
  const { t } = useTranslation();
  const [milestones, setMilestones] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('timeline');
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);

  const [newMilestone, setNewMilestone] = useState({
    title: '',
    description: '',
    category: 'personal',
    date: '',
    emotionalTags: [],
  });

  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    timeframe: 'short',
    targetDate: '',
    category: 'personal',
    subGoals: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [milestonesRes, goalsRes] = await Promise.all([
        apiClient.get('/personal/milestones'),
        apiClient.get('/personal/goals'),
      ]);
      setMilestones(milestonesRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMilestone = async () => {
    try {
      if (editingMilestone) {
        await apiClient.put(`/personal/milestones/${editingMilestone.id}`, newMilestone);
        toast.success(t('success.updated'));
        setEditingMilestone(null);
      } else {
        await apiClient.post('/personal/milestones', newMilestone);
        toast.success(t('success.saved'));
      }
      setNewMilestone({
        title: '',
        description: '',
        category: 'personal',
        date: '',
        emotionalTags: [],
      });
      setMilestoneDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const openEditMilestone = (milestone) => {
    setEditingMilestone(milestone);
    setNewMilestone({
      title: milestone.title,
      description: milestone.description,
      category: milestone.category || 'personal',
      date: milestone.date || '',
      emotionalTags: milestone.emotionalTags || [],
    });
    setMilestoneDialogOpen(true);
  };

  const handleDeleteMilestone = async (id) => {
    try {
      await apiClient.delete(`/personal/milestones/${id}`);
      toast.success(t('success.milestoneDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  };

  const handleAddGoal = async () => {
    try {
      if (editingGoal) {
        await apiClient.put(`/personal/goals/${editingGoal.id}`, newGoal);
        toast.success(t('success.goalUpdated'));
        setEditingGoal(null);
      } else {
        await apiClient.post('/personal/goals', newGoal);
        toast.success(t('success.goalAdded'));
      }
      setNewGoal({
        title: '',
        description: '',
        timeframe: 'short',
        targetDate: '',
        category: 'personal',
        subGoals: [],
      });
      setGoalDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const openEditGoal = (goal) => {
    setEditingGoal(goal);
    setNewGoal({
      title: goal.title,
      description: goal.description,
      timeframe: goal.timeframe,
      targetDate: goal.targetDate || '',
      category: goal.category || 'personal',
      subGoals: goal.subGoals || [],
    });
    setGoalDialogOpen(true);
  };

  const addSubGoal = () => {
    setNewGoal({
      ...newGoal,
      subGoals: [...(newGoal.subGoals || []), { title: '', completed: false }],
    });
  };

  const updateSubGoal = (index, field, value) => {
    const updated = [...newGoal.subGoals];
    updated[index][field] = value;
    setNewGoal({ ...newGoal, subGoals: updated });
  };

  const removeSubGoal = (index) => {
    setNewGoal({
      ...newGoal,
      subGoals: newGoal.subGoals.filter((_, i) => i !== index),
    });
  };

  const handleDeleteGoal = async (id) => {
    try {
      await apiClient.delete(`/personal/goals/${id}`);
      toast.success(t('success.goalDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  };

  return (
    <div className="p-8 space-y-8" data-testid="life-map-page">
      <div>
        <h1 className="font-heading text-4xl font-bold text-gray-900">{t('lifeMap.title')}</h1>
        <p className="text-lg text-gray-600 mt-2">
          {t('lifeMap.subtitle')}
        </p>
        {/* AI Integration Note */}
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm">
          <Sparkles className="w-4 h-4" />
          <span>Your Twin may use this information to personalize guidance and recommendations.</span>
        </div>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'timeline'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('lifeMap.timeline')}
        </button>
        <button
          onClick={() => setActiveTab('milestones')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'milestones'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('lifeMap.milestones')} ({milestones.length})
        </button>
        <button
          onClick={() => setActiveTab('goals')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'goals'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('lifeMap.goals')} ({goals.filter(g => g.status === 'active').length})
        </button>
      </div>

      {activeTab === 'timeline' && (
        <div className="space-y-6">
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            <div className="space-y-8">
              {[...milestones, ...goals].sort((a, b) => 
                new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)
              ).map((item, index) => {
                const isMilestone = !!item.date;
                const hasSubGoals = !isMilestone && item.subGoals && item.subGoals.length > 0;
                return (
                  <div key={item.id} className="relative pl-16">
                    <div className="absolute left-0 w-16 flex items-center justify-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isMilestone ? 'bg-primary/10' : 'bg-amber-100'}`}>
                        {isMilestone ? <Milestone className="w-4 h-4 text-primary" /> : <Target className="w-4 h-4 text-amber-600" />}
                      </div>
                    </div>
                    <Card 
                      className="p-4 rounded-xl cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
                      onClick={() => setViewingItem(item)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isMilestone ? 'bg-primary/10 text-primary' : 'bg-amber-100 text-amber-700'}`}>
                              {isMilestone ? t('lifeMap.milestone') : t('lifeMap.goal')}
                            </span>
                          </div>
                          <h3 className="font-semibold text-gray-900 mt-1">{item.title}</h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
                              {item.category || item.timeframe}
                            </span>
                            {item.date && (
                              <span className="text-xs text-gray-500">
                                {new Date(item.date).toLocaleDateString()}
                              </span>
                            )}
                            {item.progress > 0 && (
                              <span className="text-xs text-amber-600 font-medium">{item.progress}%</span>
                            )}
                          </div>
                          
                          {/* Sub-goals section for goals */}
                          {hasSubGoals && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-xs text-gray-500 mb-2">{t('lifeMap.subGoals')} ({item.subGoals.length})</p>
                              <div className="space-y-1.5">
                                {item.subGoals.map((subGoal, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-sm pl-2 border-l-2 border-amber-200">
                                    <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${subGoal.completed ? 'bg-green-100 border-green-500' : 'border-gray-300'}`}>
                                      {subGoal.completed && <span className="text-green-600 text-xs">✓</span>}
                                    </span>
                                    <span className={`text-sm ${subGoal.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                                      {subGoal.title}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </Card>
                  </div>
                );
              })}
              {milestones.length === 0 && goals.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>{t('lifeMap.noMilestonesGoals')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Item Dialog */}
      <Dialog open={!!viewingItem} onOpenChange={(open) => !open && setViewingItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2">
              {viewingItem?.date ? (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Milestone className="w-4 h-4 text-primary" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <Target className="w-4 h-4 text-amber-600" />
                </div>
              )}
              <DialogTitle>{viewingItem?.title}</DialogTitle>
            </div>
          </DialogHeader>
          
          {viewingItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${viewingItem.date ? 'bg-primary/10 text-primary' : 'bg-amber-100 text-amber-700'}`}>
                  {viewingItem.date ? t('lifeMap.milestone') : t('lifeMap.goal')}
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
                  {viewingItem.category || viewingItem.timeframe}
                </span>
              </div>
              
              {viewingItem.description && (
                <div>
                  <Label className="text-xs text-gray-500">{t('lifeMap.description')}</Label>
                  <p className="text-sm text-gray-700 mt-1">{viewingItem.description}</p>
                </div>
              )}
              
              {viewingItem.date && (
                <div>
                  <Label className="text-xs text-gray-500">{t('lifeMap.date')}</Label>
                  <p className="text-sm text-gray-700 mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {new Date(viewingItem.date).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              {viewingItem.targetDate && (
                <div>
                  <Label className="text-xs text-gray-500">{t('lifeMap.targetDate')}</Label>
                  <p className="text-sm text-gray-700 mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {new Date(viewingItem.targetDate).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              {viewingItem.timeframe && (
                <div>
                  <Label className="text-xs text-gray-500">{t('lifeMap.timeframe')}</Label>
                  <p className="text-sm text-gray-700 mt-1">{t(`lifeMap.timeframes.${viewingItem.timeframe}`)}</p>
                </div>
              )}
              
              {viewingItem.progress !== undefined && viewingItem.progress > 0 && (
                <div>
                  <Label className="text-xs text-gray-500">{t('lifeMap.progress')}</Label>
                  <div className="mt-2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500"
                        style={{ width: `${viewingItem.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{viewingItem.progress}% {t('lifeMap.complete')}</p>
                  </div>
                </div>
              )}
              
              {viewingItem.subGoals && viewingItem.subGoals.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-500">{t('lifeMap.subGoals')}</Label>
                  <ul className="mt-2 space-y-1">
                    {viewingItem.subGoals.map((subGoal, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center ${subGoal.completed ? 'bg-green-100 border-green-500' : 'border-gray-300'}`}>
                          {subGoal.completed && <span className="text-green-600 text-xs">✓</span>}
                        </span>
                        <span className={subGoal.completed ? 'text-gray-400 line-through' : 'text-gray-700'}>{subGoal.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {viewingItem.emotionalTags && viewingItem.emotionalTags.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-500">{t('lifeMap.emotionalTags')}</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {viewingItem.emotionalTags.map((tag, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    if (viewingItem.date) {
                      openEditMilestone(viewingItem);
                    } else {
                      openEditGoal(viewingItem);
                    }
                    setViewingItem(null);
                  }}
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  {t('common.edit')}
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    if (viewingItem.date) {
                      handleDeleteMilestone(viewingItem.id);
                    } else {
                      handleDeleteGoal(viewingItem.id);
                    }
                    setViewingItem(null);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {activeTab === 'milestones' && (
        <div className="space-y-6">
          <Dialog open={milestoneDialogOpen} onOpenChange={(open) => {
            setMilestoneDialogOpen(open);
            if (!open) {
              setEditingMilestone(null);
              setNewMilestone({
                title: '',
                description: '',
                category: 'personal',
                date: '',
                emotionalTags: [],
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t('lifeMap.addMilestone')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingMilestone ? t('lifeMap.editMilestone') : t('lifeMap.addMilestone')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t('forms.titleOptional')}</Label>
                  <Input
                    value={newMilestone.title}
                    onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                    placeholder={t('lifeMap.milestonePlaceholder')}
                  />
                </div>
                <div>
                  <Label>{t('vault.description')}</Label>
                  <Textarea
                    value={newMilestone.description}
                    onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                    placeholder={t('forms.tellStory')}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>{t('labels.dateRequired')}</Label>
                  <Input
                    type="date"
                    value={newMilestone.date}
                    onChange={(e) => setNewMilestone({ ...newMilestone, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t('lifeMap.category')}</Label>
                  <select
                    value={newMilestone.category}
                    onChange={(e) => setNewMilestone({ ...newMilestone, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="personal">{t('lifeMap.categories.personal')}</option>
                    <option value="career">{t('lifeMap.categories.career')}</option>
                    <option value="education">{t('lifeMap.categories.education')}</option>
                    <option value="relationship">{t('lifeMap.categories.relationship')}</option>
                    <option value="health">{t('lifeMap.categories.health')}</option>
                  </select>
                </div>
                <Button onClick={handleAddMilestone} className="w-full" disabled={!newMilestone.title || !newMilestone.date}>
                  {editingMilestone ? t('lifeMap.updateMilestone') : t('lifeMap.addMilestone')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {milestones.map((milestone) => (
              <Card key={milestone.id} className="p-4 rounded-xl">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{milestone.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
                        {milestone.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(milestone.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditMilestone(milestone)}
                    >
                      <Edit2 className="w-4 h-4 text-gray-400 hover:text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteMilestone(milestone.id)}
                    >
                      <Trash2 className="w-4 h-4 text-gray-400 hover:text-error" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'goals' && (
        <div className="space-y-6">
          <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t('lifeMap.addGoal')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingGoal ? t('lifeMap.editGoal') : t('lifeMap.addGoal')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t('labels.titleRequired')}</Label>
                  <Input
                    value={newGoal.title}
                    onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                    placeholder={t('lifeMap.goalPlaceholder')}
                  />
                </div>
                <div>
                  <Label>{t('labels.descriptionOptional')}</Label>
                  <Textarea
                    value={newGoal.description}
                    onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                    placeholder={t('lifeMap.goalDescPlaceholder')}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>{t('lifeMap.timeframe')}</Label>
                  <select
                    value={newGoal.timeframe}
                    onChange={(e) => setNewGoal({ ...newGoal, timeframe: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="short">{t('lifeMap.timeframes.short')}</option>
                    <option value="medium">{t('lifeMap.timeframes.medium')}</option>
                    <option value="long">{t('lifeMap.timeframes.long')}</option>
                  </select>
                </div>
                <div>
                  <Label>{t('lifeMap.targetDate')}</Label>
                  <Input
                    type="date"
                    value={newGoal.targetDate}
                    onChange={(e) => setNewGoal({ ...newGoal, targetDate: e.target.value })}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>{t('lifeMap.subGoals')}</Label>
                    <Button variant="outline" size="sm" onClick={addSubGoal} type="button">
                      <Plus className="w-3 h-3 mr-1" />
                      {t('lifeMap.addSubGoal')}
                    </Button>
                  </div>
                  {newGoal.subGoals?.length > 0 && (
                    <div className="space-y-2">
                      {newGoal.subGoals.map((subGoal, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            value={subGoal.title}
                            onChange={(e) => updateSubGoal(index, 'title', e.target.value)}
                            placeholder={`${t('lifeMap.subGoalPlaceholder')} ${index + 1}`}
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSubGoal(index)}
                            type="button"
                          >
                            <Trash2 className="w-4 h-4 text-gray-400" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button onClick={handleAddGoal} className="w-full" disabled={!newGoal.title}>
                  {editingGoal ? t('lifeMap.updateGoal') : t('lifeMap.addGoal')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {goals.filter(g => g.status === 'active').map((goal) => (
              <Card key={goal.id} className="p-4 rounded-xl">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{goal.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary capitalize">
                        {goal.timeframe}
                      </span>
                      {goal.targetDate && (
                        <span className="text-xs text-gray-500">
                          {t('lifeMap.target')}: {new Date(goal.targetDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {goal.progress > 0 && (
                      <div className="mt-2">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-personal-sage"
                            style={{ width: `${goal.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{goal.progress}% {t('lifeMap.complete')}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditGoal(goal)}
                    >
                      <Edit2 className="w-4 h-4 text-gray-400 hover:text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteGoal(goal.id)}
                    >
                      <Trash2 className="w-4 h-4 text-gray-400 hover:text-error" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
